/**
 * The router-bound half of `overlay.ts`.
 *
 * A screen opens a sheet by navigating, never by calling `setState`:
 *
 *   const { isOpen, open, close } = useOverlay();
 *   <Button onClick={() => open({ kind: "sheet", name: "create-pool" })}>…
 *   <Sheet open={isOpen("sheet", "create-pool")} onOpenChange={(o) => !o && close()} />
 *
 * `replace: true` on close is deliberate — closing an overlay should not push a
 * second history entry, or a fan has to press back twice to get off the screen.
 * Opening pushes, so back closes.
 */
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";

import {
  closeOverlay,
  isOverlayOpen,
  openOverlay,
  topOverlay,
  type typeOverlayDescriptor,
  type typeOverlayKind,
  type typeOverlaySearch,
} from "./overlay";

type typeLooseSearch = typeOverlaySearch & Record<string, unknown>;

export type typeUseOverlayResult = {
  /** The overlay a back press would close, or null when none is open. */
  overlay: typeOverlayDescriptor | null;
  isOpen: (kind: typeOverlayKind, name: string) => boolean;
  open: (
    overlay: typeOverlayDescriptor & { params?: Record<string, string> },
  ) => void;
  close: (options?: {
    kind?: typeOverlayKind;
    clearParams?: readonly string[];
  }) => void;
};

export function useOverlay(): typeUseOverlayResult {
  // `strict: false` because this hook is shared by every screen and must not
  // be pinned to one route id. The overlay keys it reads are the same on all of
  // them by contract, which is exactly why they are named once in `overlay.ts`.
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const open = useCallback<typeUseOverlayResult["open"]>(
    (overlay) => {
      void navigate({
        to: ".",
        search: (previous: unknown) =>
          openOverlay((previous ?? {}) as typeLooseSearch, overlay),
      });
    },
    [navigate],
  );

  const close = useCallback<typeUseOverlayResult["close"]>(
    (options) => {
      void navigate({
        to: ".",
        replace: true,
        search: (previous: unknown) =>
          closeOverlay((previous ?? {}) as typeLooseSearch, options),
      });
    },
    [navigate],
  );

  const isOpen = useCallback<typeUseOverlayResult["isOpen"]>(
    (kind, name) => isOverlayOpen(search, kind, name),
    [search],
  );

  const overlay = useMemo(() => topOverlay(search), [search]);

  return { overlay, isOpen, open, close };
}
